import React from 'react';
import EventCard from '../components/EventCard';
import ProfileCard from '../components/ProfileCard';
import VerificationBadge, { VerificationBadgeGroup } from '../components/VerificationBadge';
import AvatarWithBadge, { AvatarGroup } from '../components/AvatarWithBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

/**
 * DesignSystemDemo - Showcase page for Dostination design system components
 * Displays all custom components with various configurations
 */
const DesignSystemDemo = () => {
  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-display text-5xl font-bold text-primary-700">
            Dostination Design System
          </h1>
          <p className="text-body text-xl text-neutral-600 max-w-2xl mx-auto">
            A luxury travel companion platform with elegant, trust-focused design components
          </p>
        </div>

        {/* Color Palette */}
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-3xl">Color Palette</CardTitle>
            <CardDescription>Brand colors with shades</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Primary Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-primary-700">Primary (Trust Blue-Gray)</h3>
              <div className="flex gap-2 flex-wrap">
                {[50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map(shade => (
                  <div key={shade} className="text-center">
                    <div className={`w-20 h-20 rounded-lg bg-primary-${shade} shadow-soft`} />
                    <p className="text-xs mt-1">{shade}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Secondary Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-secondary-700">Secondary (Champagne Gold)</h3>
              <div className="flex gap-2 flex-wrap">
                {[50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map(shade => (
                  <div key={shade} className="text-center">
                    <div className={`w-20 h-20 rounded-lg bg-secondary-${shade} shadow-gold`} />
                    <p className="text-xs mt-1">{shade}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Accent Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-accent-700">Accent (Warm Coral-Red)</h3>
              <div className="flex gap-2 flex-wrap">
                {[50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map(shade => (
                  <div key={shade} className="text-center">
                    <div className={`w-20 h-20 rounded-lg bg-accent-${shade} shadow-accent`} />
                    <p className="text-xs mt-1">{shade}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Success Colors */}
            <div>
              <h3 className="text-lg font-semibold mb-3 text-success-700">Success (Prosperity Green)</h3>
              <div className="flex gap-2 flex-wrap">
                {[50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map(shade => (
                  <div key={shade} className="text-center">
                    <div className={`w-20 h-20 rounded-lg bg-success-${shade} shadow-soft`} />
                    <p className="text-xs mt-1">{shade}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Typography */}
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-3xl">Typography</CardTitle>
            <CardDescription>Font families and styles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-neutral-500 mb-2">Display Font (Playfair Display)</p>
              <h1 className="text-display text-4xl font-bold text-primary-700">The quick brown fox jumps</h1>
            </div>
            <div>
              <p className="text-sm text-neutral-500 mb-2">Body Font (Inter)</p>
              <p className="text-body text-lg text-neutral-700">The quick brown fox jumps over the lazy dog</p>
            </div>
          </CardContent>
        </Card>

        {/* Verification Badges */}
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-3xl">Verification Badges</CardTitle>
            <CardDescription>Trust and verification indicators</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Default Style</h3>
              <div className="flex flex-wrap gap-3">
                <VerificationBadge type="id" />
                <VerificationBadge type="background" />
                <VerificationBadge type="premium" />
                <VerificationBadge type="verified" />
                <VerificationBadge type="trusted" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Outline Style</h3>
              <div className="flex flex-wrap gap-3">
                <VerificationBadge type="id" variant="outline" />
                <VerificationBadge type="background" variant="outline" />
                <VerificationBadge type="premium" variant="outline" />
                <VerificationBadge type="verified" variant="outline" />
                <VerificationBadge type="trusted" variant="outline" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <VerificationBadge type="id" size="sm" />
                <VerificationBadge type="id" size="md" />
                <VerificationBadge type="id" size="lg" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Badge Group</h3>
              <VerificationBadgeGroup
                badges={[
                  { type: 'id' },
                  { type: 'background' },
                  { type: 'premium' },
                ]}
                variant="outline"
              />
            </div>
          </CardContent>
        </Card>

        {/* Avatar with Badge */}
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-3xl">Avatar with Badge</CardTitle>
            <CardDescription>User avatars with verification indicators</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Sizes</h3>
              <div className="flex flex-wrap items-end gap-6">
                <AvatarWithBadge size="xs" fallback="JD" verified verificationType="id" />
                <AvatarWithBadge size="sm" fallback="JD" verified verificationType="background" />
                <AvatarWithBadge size="md" fallback="JD" verified verificationType="premium" />
                <AvatarWithBadge size="lg" fallback="JD" verified verificationType="trusted" />
                <AvatarWithBadge size="xl" fallback="JD" verified verificationType="id" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Verification Types</h3>
              <div className="flex flex-wrap gap-6">
                <AvatarWithBadge size="lg" fallback="ID" verified verificationType="id" />
                <AvatarWithBadge size="lg" fallback="BG" verified verificationType="background" />
                <AvatarWithBadge size="lg" fallback="PM" verified verificationType="premium" />
                <AvatarWithBadge size="lg" fallback="TR" verified verificationType="trusted" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Avatar Group</h3>
              <AvatarGroup
                avatars={[
                  { fallback: 'JD', verified: true, verificationType: 'id' },
                  { fallback: 'SM', verified: true, verificationType: 'premium' },
                  { fallback: 'AK', verified: true, verificationType: 'background' },
                  { fallback: 'LM', verified: false },
                  { fallback: 'RP', verified: true, verificationType: 'trusted' },
                  { fallback: 'TW', verified: false },
                  { fallback: 'NK', verified: true, verificationType: 'id' },
                ]}
                max={5}
                size="md"
              />
            </div>
          </CardContent>
        </Card>

        {/* Profile Cards */}
        <div>
          <h2 className="text-display text-3xl font-bold text-primary-700 mb-6">Profile Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ProfileCard
              name="Sarah Johnson"
              username="sarahj"
              avatarUrl="https://i.pravatar.cc/150?img=1"
              bio="Adventure seeker and culture enthusiast. Love exploring hidden gems and meeting new people!"
              location="San Francisco, CA"
              memberSince="Jan 2023"
              rating={4.9}
              reviewCount={127}
              eventsHosted={45}
              eventsAttended={89}
              verifications={['id', 'background', 'premium']}
              isPremium={true}
              onViewProfile={() => console.log('View profile')}
              onMessage={() => console.log('Message')}
            />
            <ProfileCard
              name="Michael Chen"
              username="mikec"
              avatarUrl="https://i.pravatar.cc/150?img=12"
              bio="Tech professional who loves weekend getaways and foodie adventures."
              location="New York, NY"
              memberSince="Mar 2023"
              rating={4.7}
              reviewCount={83}
              eventsHosted={28}
              eventsAttended={56}
              verifications={['id', 'background']}
              onViewProfile={() => console.log('View profile')}
              onMessage={() => console.log('Message')}
            />
            <ProfileCard
              name="Emma Wilson"
              username="emmaw"
              bio="Travel blogger and photography enthusiast"
              location="London, UK"
              rating={4.8}
              reviewCount={95}
              verifications={['id']}
              variant="compact"
              onViewProfile={() => console.log('View profile')}
            />
          </div>
        </div>

        {/* Event Cards */}
        <div>
          <h2 className="text-display text-3xl font-bold text-primary-700 mb-6">Event Cards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <EventCard
              title="Wine Tasting in Napa Valley"
              description="Join us for an exclusive wine tasting experience at premium vineyards in Napa Valley. Perfect for wine enthusiasts!"
              date="June 15, 2024"
              time="2:00 PM"
              location="Napa Valley, CA"
              attendees={12}
              maxAttendees={15}
              imageUrl="https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=300&fit=crop"
              category="Food & Drink"
              price="$85"
              featured={true}
              onViewDetails={() => console.log('View details')}
              onJoin={() => console.log('Join event')}
            />
            <EventCard
              title="Sunset Hiking Adventure"
              description="Experience breathtaking sunset views on this guided hiking trail. All skill levels welcome!"
              date="June 20, 2024"
              time="5:30 PM"
              location="Marin Headlands, CA"
              attendees={8}
              maxAttendees={12}
              imageUrl="https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop"
              category="Outdoor"
              price="$35"
              onViewDetails={() => console.log('View details')}
              onJoin={() => console.log('Join event')}
            />
            <EventCard
              title="Art Gallery Opening"
              description="Exclusive preview of contemporary art exhibition featuring local artists."
              date="June 25, 2024"
              time="7:00 PM"
              location="SFMOMA, San Francisco"
              attendees={45}
              maxAttendees={50}
              imageUrl="https://images.unsplash.com/photo-1531243269054-5ebf6f34081e?w=400&h=300&fit=crop"
              category="Arts & Culture"
              price="Free"
              onViewDetails={() => console.log('View details')}
              onJoin={() => console.log('Join event')}
            />
          </div>
        </div>

        {/* Buttons */}
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-3xl">Buttons</CardTitle>
            <CardDescription>Button variants and styles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Primary Buttons</h3>
              <div className="flex flex-wrap gap-3">
                <Button className="bg-primary-500 hover:bg-primary-600">Primary</Button>
                <Button className="bg-secondary-500 hover:bg-secondary-600 text-secondary-foreground shadow-gold">Secondary</Button>
                <Button className="bg-accent-500 hover:bg-accent-600 shadow-accent">Accent</Button>
                <Button className="bg-success-500 hover:bg-success-600">Success</Button>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Outline Buttons</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" className="border-primary-500 text-primary-500 hover:bg-primary-50">Primary</Button>
                <Button variant="outline" className="border-secondary-500 text-secondary-600 hover:bg-secondary-50">Secondary</Button>
                <Button variant="outline" className="border-accent-500 text-accent-500 hover:bg-accent-50">Accent</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shadows */}
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-3xl">Custom Shadows</CardTitle>
            <CardDescription>Elevation and depth effects</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-white rounded-lg shadow-soft">
                <h3 className="font-semibold mb-2">Soft Shadow</h3>
                <p className="text-sm text-neutral-600">Subtle elevation for cards</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-gold">
                <h3 className="font-semibold mb-2">Gold Shadow</h3>
                <p className="text-sm text-neutral-600">Premium highlight effect</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-accent">
                <h3 className="font-semibold mb-2">Accent Shadow</h3>
                <p className="text-sm text-neutral-600">Attention-grabbing glow</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DesignSystemDemo;